import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Copy, Check, ChevronUp, ChevronDown, Pencil } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitizeCitations } from '@/features/chat/shared';
import { ChatMessage } from '@/features/chat/shared/components';
import { useMarkdownComponents } from '@/features/chat/shared/hooks/useMarkdownComponents';
import { stripReferences } from '@/features/chat/shared/utils/clipboard';
import { cn } from '../../../../lib/utils';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../../../../components/ui/avatar';
import { Button } from '../../../../components/ui/button';
import { TooltipProvider } from '../../../../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
} from '../../../../components/ui/collapsible';

import { RootState } from '../../../../reducers';
import { useAppDispatch } from '../../../../store';
import { onClickTimestamp } from '../../../../lib/commonUtils';
import {
  askMultimodalQuestion,
  askQuestion,
  truncateConversation,
} from '../../../selectedVideo/selectedVideoSlice';
import ChatThinking from './ChatThinking';
import ChatThinkingVideo from './ChatThinkingVideo';
import ChatError from '../ChatError';
import newLogo from '../../../../assets/icons/new_logo.svg';

const ChatHistory = () => {
  const dispatch = useAppDispatch();
  const { selectedVideo, settings } = useSelector((state: RootState) => state);
  const { status, conversation, isVideoMode, sessionId, ocrEnabled } =
    selectedVideo.chat;
  const { user } = settings;
  const { picture } = user;

  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [showThinking, setShowThinking] = useState(false);
  const [expandingResponse, setExpandingResponse] = useState<string | null>(
    null,
  );
  const [showFullResponse, setShowFullResponse] = useState(false);
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null,
  );

  // Track initial conversation length to prevent animation on history load
  // Store in a ref to persist across renders, but initialize it only once per sessionId
  const initialConversationLengthRef = React.useRef<{
    sessionId: string | undefined;
    length: number;
  }>({ sessionId: undefined, length: 0 });

  // Update the ref if sessionId changes (new chat session)
  if (initialConversationLengthRef.current.sessionId !== sessionId) {
    initialConversationLengthRef.current = {
      sessionId,
      length: conversation.length,
    };
  }

  useEffect(() => {
    if (status === 'pending') {
      setIsThinking(true);
      setExpandingResponse(null);

      // Delay showing thinking animation for first message entrance (600ms animation)
      const isFirstMessage = conversation.length === 1;
      if (isFirstMessage) {
        setTimeout(() => setShowThinking(true), 650);
      } else {
        setShowThinking(true);
      }
    } else if (status === 'succeeded' && isThinking) {
      const lastMessage = conversation[conversation.length - 1];
      if (lastMessage && lastMessage.role === 'assistant') {
        setExpandingResponse(lastMessage.content);
        setIsThinking(false);
        setShowThinking(false);
      } else {
        setIsThinking(false);
        setShowThinking(false);
      }
    } else if (status === 'failed') {
      setIsThinking(false);
      setShowThinking(false);
      setExpandingResponse(null);
    }
  }, [status, conversation, isThinking]);

  const getAvatar = (role: string) => {
    if (role === 'user') {
      return (
        <Avatar className="ml-2 w-7 h-7">
          <AvatarImage src={picture} alt="You" />
          <AvatarFallback>U</AvatarFallback>
        </Avatar>
      );
    }
    return (
      <Avatar className="bg-transparent rounded-none w-7 h-7 flex-shrink-0">
        <AvatarImage
          src={newLogo}
          alt="ScreenApp"
          className="h-full w-full object-contain"
        />
        <AvatarFallback className="bg-transparent">SA</AvatarFallback>
      </Avatar>
    );
  };

  const handleCopy = (text: string, index: number) => {
    // Strip references before copying
    const cleanText = stripReferences(text);
    navigator.clipboard.writeText(cleanText);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleTimestampClick = (seconds: number) => {
    console.log('Timestamp clicked:', seconds);
    onClickTimestamp(seconds);
  };

  const handleEditQuery = (messageIndex: number, newContent: string) => {
    const userMessage = conversation[messageIndex];
    const fileId = selectedVideo.file?._id;

    if (!userMessage || userMessage.role !== 'user' || !fileId) return;

    // Truncate conversation history at this point
    dispatch(truncateConversation(messageIndex));

    // Re-submit with edited content
    if (isVideoMode) {
      dispatch(
        askMultimodalQuestion({
          fileId,
          promptText: newContent,
          mediaAnalysisOptions: {
            video: {
              segments: [],
            },
          },
          sessionId: sessionId || undefined,
        }),
      );
    } else if (ocrEnabled) {
      dispatch(
        askMultimodalQuestion({
          fileId,
          promptText: newContent,
          mediaAnalysisOptions: {
            ocr: {
              enabled: true,
            },
          },
          sessionId: sessionId || undefined,
        }),
      );
    } else {
      dispatch(
        askQuestion({
          fileId,
          question: newContent,
          sessionId: sessionId || undefined,
        }),
      );
    }

    // Clear editing state
    setEditingMessageIndex(null);
  };

  // Use shared markdown components hook with timestamp support
  const markdownComponents = useMarkdownComponents({
    enableTimestamps: true,
    hideTimestamps: false,
    onTimestampClick: handleTimestampClick,
  });

  const renderMessage = (
    { role, content }: { role: string; content: string },
    index: number,
  ) => {
    // Sanitize content to fix citation formatting
    const sanitizedContent = sanitizeCitations(content);
    // Check if this is the very first user message in the conversation
    const isFirstUserMessage = index === 0 && role === 'user';
    // Check if this message was loaded from history (existed when session started)
    const wasLoadedFromHistory =
      index < initialConversationLengthRef.current.length;

    // Debug logging
    if (isFirstUserMessage) {
      console.log('First user message debug:', {
        index,
        sessionId,
        initialLength: initialConversationLengthRef.current.length,
        initialSessionId: initialConversationLengthRef.current.sessionId,
        wasLoadedFromHistory,
        willAnimate: !wasLoadedFromHistory,
      });
    }

    // Build actions based on message role
    let actions: Array<{
      icon: any;
      onClick: () => void;
      title: string;
      activeIcon?: any;
      isActive?: boolean;
      activeColor?: string;
    }> = [];

    if (role === 'assistant') {
      actions = [
        {
          icon: Copy,
          onClick: () => handleCopy(content, index),
          title: 'Copy message',
          activeIcon: Check,
          isActive: copiedIndex === index,
          activeColor: 'text-green-600',
        },
      ];
    } else if (role === 'user') {
      actions = [
        {
          icon: Pencil,
          onClick: () => setEditingMessageIndex(index),
          title: 'Edit message',
        },
      ];
    }

    const isEditing = editingMessageIndex === index;

    return (
      <TooltipProvider key={index}>
        <ChatMessage
          role={role as 'user' | 'assistant'}
          content={sanitizedContent}
          showAvatar={role === 'user'}
          avatarElement={getAvatar(role)}
          actions={actions}
          markdownComponents={markdownComponents}
          isEditing={isEditing}
          onEdit={(newContent) => handleEditQuery(index, newContent)}
          onCancelEdit={() => setEditingMessageIndex(null)}
          className={cn(
            role === 'user' && 'mb-4',
            isFirstUserMessage &&
              !wasLoadedFromHistory &&
              'animate-first-message-entrance',
          )}
        />
      </TooltipProvider>
    );
  };

  if (conversation.length === 0) return null;

  return (
    <div className="mt-0 pb-4 perspective-1000">
      {conversation.map((message, index) => (
        <React.Fragment key={index}>
          {renderMessage(message, index)}
          {index === conversation.length - 1 && message.role === 'user' && (
            <div
              className={cn(
                'flex items-start transition-opacity duration-500 ease-in-out',
                (showThinking && isThinking) ||
                  expandingResponse ||
                  status === 'failed'
                  ? 'opacity-100'
                  : 'opacity-0',
              )}
            >
              <div className="flex-grow">
                {status === 'failed' && <ChatError isVideoMode={isVideoMode} />}
                {showThinking && isThinking && status !== 'failed' && (
                  <>{isVideoMode ? <ChatThinkingVideo /> : <ChatThinking />}</>
                )}
                <Collapsible
                  open={!isThinking && expandingResponse !== null}
                  onOpenChange={(open) => !open && setShowFullResponse(true)}
                >
                  <CollapsibleContent className="data-[state=open]:animate-none data-[state=closed]:animate-none">
                    {expandingResponse && (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {expandingResponse}
                      </ReactMarkdown>
                    )}
                  </CollapsibleContent>
                </Collapsible>
                {!isThinking && expandingResponse && (
                  <div className="flex justify-center mt-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowFullResponse(!showFullResponse)}
                      className="h-6 w-6 rounded-full text-muted-foreground/60 hover:text-foreground hover:bg-accent/50 transition-all duration-200"
                      aria-label={showFullResponse ? 'Show less' : 'Show more'}
                    >
                      {showFullResponse ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ChatHistory;
